import { useCallback, useSyncExternalStore } from "react";
import { api } from "../../../shared/api/client";

type PushConfig = { publicKey: string | null };

function keyBytes(key: string): Uint8Array {
  const padded = key.replace(/-/g, "+").replace(/_/g, "/").padEnd(Math.ceil(key.length / 4) * 4, "=");
  return Uint8Array.from(atob(padded), (c) => c.charCodeAt(0));
}

/**
 * Состояние пуша — одно на вкладку (09.10.2026). Кнопка живёт сразу в трёх
 * местах: подсказка «Монтажа», нижняя панель и боковое меню. Свой useState в
 * каждом давал три запроса подписки на загрузке и расходился: включил в
 * панели — подсказка всё ещё просит включить.
 */
type State = { publicKey: string | null; enabled: boolean; busy: boolean; error: string };
let state: State = { publicKey: null, enabled: false, busy: false, error: "" };
const listeners = new Set<() => void>();
const set = (patch: Partial<State>) => { state = { ...state, ...patch }; listeners.forEach((l) => l()); };
const subscribe = (l: () => void) => { listeners.add(l); return () => { listeners.delete(l); }; };

const supported = typeof window !== "undefined" && "serviceWorker" in navigator && "PushManager" in window && "Notification" in window;

let started = false;
function start() {
  if (started || !supported) return;
  started = true;
  void (async () => {
    try {
      const config = await api.get<PushConfig>("/montage/push/config");
      set({ publicKey: config.publicKey });
      if (!config.publicKey) return;
      const registration = await navigator.serviceWorker.register("/montage-sw.js", { scope: "/" });
      const subscription = await registration.pushManager.getSubscription();
      if (subscription) {
        try {
          // Re-bind on every login so a browser shared by two people never
          // silently keeps the previous account's destination.
          await api.post("/montage/push/subscriptions", subscription.toJSON());
          set({ enabled: true });
        } catch {
          await subscription.unsubscribe();
          set({ enabled: false });
        }
      }
    } catch {
      // Конфиг не получен (сеть, выход) — попробуем при следующем монтировании.
      started = false;
    }
  })();
}

export function usePush() {
  start();
  const s = useSyncExternalStore(subscribe, () => state, () => state);

  /** Возвращает текст ошибки — пусто, если включилось. */
  const enable = useCallback(async (): Promise<string> => {
    if (!state.publicKey) { set({ error: "Push пока не настроен на сервере." }); return state.error; }
    set({ busy: true, error: "" });
    try {
      // On iOS this call must follow the user's tap, in the installed Home Screen app.
      const permission = await Notification.requestPermission();
      if (permission !== "granted") throw new Error("Разрешите уведомления для приложения в настройках устройства.");
      const registration = await navigator.serviceWorker.register("/montage-sw.js", { scope: "/" });
      const subscription = await registration.pushManager.getSubscription()
        ?? await registration.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: keyBytes(state.publicKey) });
      await api.post("/montage/push/subscriptions", subscription.toJSON());
      set({ enabled: true });
    } catch (err) { set({ error: (err as Error).message }); }
    finally { set({ busy: false }); }
    return state.error;
  }, []);

  const disable = useCallback(async () => {
    set({ busy: true, error: "" });
    try {
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.getSubscription();
      if (subscription) {
        await api.delete(`/montage/push/subscriptions?endpoint=${encodeURIComponent(subscription.endpoint)}`);
        await subscription.unsubscribe();
      }
      set({ enabled: false });
    } catch (err) { set({ error: (err as Error).message }); }
    finally { set({ busy: false }); }
  }, []);

  return { supported: supported && Boolean(s.publicKey), enabled: s.enabled, busy: s.busy, error: s.error, enable, disable };
}

/** iPhone в Safari: пушей нет, пока сайт не открыт с экрана «Домой». */
export function needsHomeScreen(): boolean {
  if (typeof navigator === "undefined") return false;
  const ios = /iPhone|iPad|iPod/.test(navigator.userAgent);
  const standalone = window.matchMedia?.("(display-mode: standalone)").matches
    || (navigator as Navigator & { standalone?: boolean }).standalone === true;
  return ios && !standalone;
}
